import React from 'react';
import { useMagnesiumOxideStore, TRIAL_PRESETS, TrialId } from '../store/useMagnesiumOxideStore';
import {
  Scale,
  Sparkles,
  ChevronsDown,
  ArrowRight,
  Flame,
  Pause,
  Save,
  RotateCcw,
  Snowflake,
  ArrowUp,
  ArrowDown,
  Repeat,
  Pencil,
} from 'lucide-react';

export function MagnesiumOxideProcedure() {
  const labStage = useMagnesiumOxideStore(s => s.labStage);
  const currentTrial = useMagnesiumOxideStore(s => s.currentTrial);
  const currentReadingG = useMagnesiumOxideStore(s => s.currentReadingG);
  const crucibleTempC = useMagnesiumOxideStore(s => s.crucibleTempC);
  const mgInCrucibleG = useMagnesiumOxideStore(s => s.mgInCrucibleG);
  const mgOInCrucibleG = useMagnesiumOxideStore(s => s.mgOInCrucibleG);
  const lidIsOn = useMagnesiumOxideStore(s => s.lidIsOn);
  const lidLiftCount = useMagnesiumOxideStore(s => s.lidLiftCount);
  const fumeLossG = useMagnesiumOxideStore(s => s.fumeLossG);
  const isHeating = useMagnesiumOxideStore(s => s.isHeating);
  const results = useMagnesiumOxideStore(s => s.results);

  const isPolishingMg = useMagnesiumOxideStore(s => s.isPolishingMg);
  const isPlacingMg = useMagnesiumOxideStore(s => s.isPlacingMg);
  const isPlacingOnTripod = useMagnesiumOxideStore(s => s.isPlacingOnTripod);
  const isPlacingLid = useMagnesiumOxideStore(s => s.isPlacingLid);
  const isLiftingLid = useMagnesiumOxideStore(s => s.isLiftingLid);
  const isIgniting = useMagnesiumOxideStore(s => s.isIgniting);
  const isCooling = useMagnesiumOxideStore(s => s.isCooling);
  const isReheating = useMagnesiumOxideStore(s => s.isReheating);
  const isWeighing = useMagnesiumOxideStore(s => s.isWeighing);

  const selectTrial = useMagnesiumOxideStore(s => s.selectTrial);
  const weighCrucibleAndLid = useMagnesiumOxideStore(s => s.weighCrucibleAndLid);
  const polishMagnesium = useMagnesiumOxideStore(s => s.polishMagnesium);
  const placeMagnesiumInCrucible = useMagnesiumOxideStore(s => s.placeMagnesiumInCrucible);
  const weighCrucibleWithMg = useMagnesiumOxideStore(s => s.weighCrucibleWithMg);
  const placeOnTripod = useMagnesiumOxideStore(s => s.placeOnTripod);
  const toggleHeating = useMagnesiumOxideStore(s => s.toggleHeating);
  const placeLid = useMagnesiumOxideStore(s => s.placeLid);
  const liftLid = useMagnesiumOxideStore(s => s.liftLid);
  const startFinalHeating = useMagnesiumOxideStore(s => s.startFinalHeating);
  const startCooling = useMagnesiumOxideStore(s => s.startCooling);
  const weighFinal = useMagnesiumOxideStore(s => s.weighFinal);
  const checkConstantMass = useMagnesiumOxideStore(s => s.checkConstantMass);
  const recordObservation = useMagnesiumOxideStore(s => s.recordObservation);
  const resetTrial = useMagnesiumOxideStore(s => s.resetTrial);

  const preset = TRIAL_PRESETS[currentTrial];

  const animating =
    isPolishingMg || isPlacingMg || isPlacingOnTripod || isPlacingLid ||
    isLiftingLid || isIgniting || isReheating || isWeighing;

  const canWeighFinal = labStage === 'cooling' && crucibleTempC < 60;
  const showLidLiftPenaltyWarning = lidLiftCount >= 4;

  return (
    <div className="flex flex-col h-full pointer-events-auto">
      <div className="pb-4 mb-4 border-b border-border/50">
        <h2 className="text-xl font-bold bg-gradient-to-r from-amber-500 to-red-500 bg-clip-text text-transparent flex items-center gap-2">
          Empirical Formula of MgO
        </h2>
        <p className="text-sm text-muted-foreground mt-1 flex items-center gap-1">
          <Flame className="w-3 h-3" /> Trial {currentTrial}/3 — {preset.ribbonLengthCm} cm Mg ({preset.mgMassG.toFixed(2)} g)
        </p>
      </div>

      <div className="flex-1 overflow-y-auto custom-scrollbar pr-2">
        <div className="mb-4">
          <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Select Trial</h3>
          <div className="grid grid-cols-3 gap-2">
            {([1, 2, 3] as TrialId[]).map(id => {
              const done = results.some(r => r.trialId === id);
              const active = currentTrial === id;
              return (
                <button
                  key={id}
                  onClick={() => selectTrial(id)}
                  className={`py-2 rounded-lg text-sm font-semibold border transition-all ${
                    active ? 'bg-amber-500 text-white border-amber-500' :
                    done ? 'bg-emerald-500/10 text-emerald-500 border-emerald-500/30' :
                    'bg-card text-foreground border-border hover:border-amber-500/40'
                  }`}
                >
                  {TRIAL_PRESETS[id].ribbonLengthCm} cm{done ? ' ✓' : ''}
                </button>
              );
            })}
          </div>
        </div>

        <div className="mb-4 p-3 rounded-lg bg-card border border-border space-y-2 text-sm">
          <div className="flex justify-between"><span className="text-muted-foreground">Stage</span><span className="font-semibold capitalize">{labStage.replace(/-/g, ' ')}</span></div>
          <div className="flex justify-between"><span className="text-muted-foreground">Balance reading</span><span className="font-mono">{currentReadingG.toFixed(2)} g</span></div>
          <div className="flex justify-between"><span className="text-muted-foreground">Crucible temp</span><span className="font-mono">{crucibleTempC.toFixed(0)} °C</span></div>
          <div className="flex justify-between"><span className="text-muted-foreground">Mg remaining</span><span className="font-mono text-blue-500">{mgInCrucibleG.toFixed(3)} g</span></div>
          <div className="flex justify-between"><span className="text-muted-foreground">MgO formed</span><span className="font-mono text-emerald-500">{mgOInCrucibleG.toFixed(3)} g</span></div>
          <div className="flex justify-between"><span className="text-muted-foreground">Lid</span><span className="font-mono">{lidIsOn ? 'On' : 'Off'} ({lidLiftCount} lifts)</span></div>
          <div className="flex justify-between"><span className="text-muted-foreground">Fumes escaped</span><span className={`font-mono ${fumeLossG > 0.01 ? 'text-red-500' : 'text-muted-foreground'}`}>{fumeLossG.toFixed(3)} g</span></div>
        </div>

        <div className="space-y-3">
          <button
            disabled={labStage !== 'setup' || animating}
            onClick={weighCrucibleAndLid}
            className="w-full py-3 rounded-xl bg-slate-500 hover:bg-slate-600 text-white font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-md shadow-slate-500/20"
          >
            <Scale className="w-4 h-4" /> {isWeighing && labStage === 'setup' ? 'Weighing on the balance…' : '1. Weigh Empty Crucible + Lid'}
          </button>

          <button
            disabled={labStage !== 'crucible-weighed' || animating}
            onClick={polishMagnesium}
            className="w-full py-3 rounded-xl bg-yellow-500 hover:bg-yellow-600 text-white font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-md shadow-yellow-500/20"
          >
            <Sparkles className="w-4 h-4" /> {isPolishingMg ? 'Polishing…' : '2. Polish Mg with Sandpaper'}
          </button>

          <button
            disabled={labStage !== 'mg-polished' || animating}
            onClick={placeMagnesiumInCrucible}
            className="w-full py-3 rounded-xl bg-violet-500 hover:bg-violet-600 text-white font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-md shadow-violet-500/20"
          >
            <ChevronsDown className="w-4 h-4" /> {isPlacingMg ? 'Placing Mg…' : '3. Coil & Place Mg in Crucible'}
          </button>

          <button
            disabled={labStage !== 'mg-placed' || animating}
            onClick={weighCrucibleWithMg}
            className="w-full py-3 rounded-xl bg-slate-500 hover:bg-slate-600 text-white font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-md shadow-slate-500/20"
          >
            <Scale className="w-4 h-4" /> {isWeighing && labStage === 'mg-placed' ? 'Weighing on the balance…' : '4. Weigh Crucible + Lid + Mg'}
          </button>

          <button
            disabled={labStage !== 'mg-weighed' || animating}
            onClick={placeOnTripod}
            className="w-full py-3 rounded-xl bg-indigo-500 hover:bg-indigo-600 text-white font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-md shadow-indigo-500/20"
          >
            <ArrowRight className="w-4 h-4" /> {isPlacingOnTripod ? 'Setting up apparatus…' : '5. Set Crucible on Tripod'}
          </button>

          {(labStage === 'apparatus-ready' || labStage === 'heating-open' || labStage === 'igniting' || labStage === 'lid-on' || labStage === 'final-heating') && (
            <button
              onClick={toggleHeating}
              disabled={labStage === 'final-heating' && !isHeating}
              className={`w-full py-3 rounded-xl font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed ${
                isHeating
                  ? 'bg-red-500/10 text-red-500 border border-red-500/50'
                  : 'bg-orange-500 hover:bg-orange-600 text-white shadow-md shadow-orange-500/20'
              }`}
            >
              {isHeating ? <><Pause className="w-4 h-4" /> Stop Bunsen</> : <><Flame className="w-4 h-4" /> 6. Light Bunsen (heat strongly)</>}
            </button>
          )}

          {(labStage === 'igniting' || (labStage === 'heating-open' && mgInCrucibleG === preset.mgMassG)) && !lidIsOn && (
            <button
              disabled={animating || isPlacingLid}
              onClick={placeLid}
              className="w-full py-3 rounded-xl bg-amber-500 hover:bg-amber-600 text-white font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-md shadow-amber-500/20"
            >
              <ArrowDown className="w-4 h-4" /> {isPlacingLid ? 'Placing lid…' : '7. Place Lid (trap white fumes)'}
            </button>
          )}

          {lidIsOn && labStage === 'lid-on' && (
            <button
              disabled={animating}
              onClick={liftLid}
              className={`w-full py-3 rounded-xl font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed ${
                showLidLiftPenaltyWarning
                  ? 'bg-rose-500/15 text-rose-500 border border-rose-500/40'
                  : 'bg-sky-500 hover:bg-sky-600 text-white shadow-md shadow-sky-500/20'
              }`}
            >
              <ArrowUp className="w-4 h-4" /> {isLiftingLid ? 'Lifting briefly…' : `8. Lift Lid Briefly (let air in) — ${lidLiftCount} times`}
            </button>
          )}

          {labStage === 'lid-on' && (
            <button
              disabled={animating}
              onClick={startFinalHeating}
              className="w-full py-3 rounded-xl bg-orange-600 hover:bg-orange-700 text-white font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-md shadow-orange-500/20"
            >
              <Flame className="w-4 h-4" /> 9. Remove Lid for Final Strong Heat
            </button>
          )}

          {labStage === 'final-heating' && (
            <button
              disabled={animating}
              onClick={startCooling}
              className="w-full py-3 rounded-xl bg-sky-500 hover:bg-sky-600 text-white font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-md shadow-sky-500/20"
            >
              <Snowflake className="w-4 h-4" /> 10. Stop Heating & Cool Crucible
            </button>
          )}

          {(labStage === 'cooling' || labStage === 'final-weighed') && (
            <button
              disabled={labStage === 'final-weighed' || !canWeighFinal || animating}
              onClick={weighFinal}
              className="w-full py-3 rounded-xl bg-slate-500 hover:bg-slate-600 text-white font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-md shadow-slate-500/20"
            >
              <Scale className="w-4 h-4" /> {labStage === 'final-weighed' ? '11. ✓ Final mass recorded' : isWeighing ? 'Weighing on the balance…' : canWeighFinal ? '11. Weigh Crucible + Lid + MgO' : `Wait for crucible to cool… ${crucibleTempC.toFixed(0)}°C`}
            </button>
          )}

          {labStage === 'final-weighed' && (
            <button
              disabled={animating}
              onClick={checkConstantMass}
              className="w-full py-3 rounded-xl bg-purple-500 hover:bg-purple-600 text-white font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-md shadow-purple-500/20"
            >
              <Repeat className="w-4 h-4" /> {isReheating ? 'Reheating to check…' : '12. Reheat & Confirm Constant Mass'}
            </button>
          )}

          {labStage === 'constant-mass' && (
            <button
              disabled={animating}
              onClick={recordObservation}
              className="w-full py-3 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-md shadow-emerald-500/20"
            >
              <Save className="w-4 h-4" /> 13. Record & Calculate Empirical Formula
            </button>
          )}

          {(labStage === 'observed' || labStage === 'completed') && (
            <div className="p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/30 text-sm text-emerald-700 dark:text-emerald-300">
              <p className="font-bold flex items-center gap-2"><Pencil className="w-4 h-4"/> Trial {currentTrial} recorded.</p>
              <p className="mt-1 text-xs">Open the <em>Data</em> tab to see the mole ratio, or pick another trial above.</p>
            </div>
          )}

          <button
            onClick={resetTrial}
            className="w-full py-2 rounded-xl text-muted-foreground hover:text-foreground hover:bg-muted/60 text-sm flex items-center justify-center gap-2"
          >
            <RotateCcw className="w-3 h-3" /> Reset Trial
          </button>
        </div>

        <div className="mt-6 bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
          <h4 className="text-amber-600 dark:text-amber-400 text-xs font-bold uppercase tracking-wider mb-2">Tips</h4>
          <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside ml-2">
            <li>Polishing removes the dull oxide layer so only pure Mg is weighed.</li>
            <li>Place the lid quickly once Mg starts burning to trap MgO fumes.</li>
            <li>Lift the lid briefly only 1–3 times to let air in — too many lifts lose mass.</li>
            <li>Repeat heating until the mass is constant — that means all Mg has reacted.</li>
            <li>The expected mole ratio of Mg : O is <span className="font-mono font-bold">1 : 1</span> → empirical formula <span className="font-mono font-bold">MgO</span>.</li>
          </ul>
        </div>
      </div>

    </div>
  );
}

export default MagnesiumOxideProcedure;
